
import { AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { AttachmentDto, TestAttachmentDto, ProjectDetailsService, UpdateAttachmentDto } from '../../services/project-details.service';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { DataGridComponent } from '../../shared/data-grid/data-grid.component';
import { DeleteConfirmationDialogComponent } from '../../projects-dashboard/well-attachments-dialog/delete-confirmation-dialog/delete-confirmation-dialog.component';

@Component({
  selector: 'app-attachment-component',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTableModule, MatSortModule, MatProgressSpinnerModule, SpinnerComponent, DataGridComponent],
  templateUrl: './attachment-component.html',
  styleUrls: ['./attachment-component.css']
})
export class AttachmentComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('projectSort') projectSort!: MatSort;
  @ViewChild('testSort') testSort!: MatSort;
  @Input() projectId!: number;
  attachments: AttachmentDto[] = [];
  testAttachments: TestAttachmentDto[] = [];
  projectDataSource = new MatTableDataSource<AttachmentDto>([]);
  testDataSource = new MatTableDataSource<TestAttachmentDto>([]);
  readonly projectColumns: string[] = ['fileName', 'uploadedBy', 'description', 'actions'];
  readonly testColumns: string[] = ['slurryName', 'testTypeName', 'fileName', 'uploadedBy', 'description', 'actions'];
  

  projectGridColumns = [
    { key: 'fileName', label: 'Document Name', sortable: true },
    { key: 'uploadedBy', label: 'Uploaded By', sortable: true },
    { key: 'description', label: 'Description', sortable: true }
  ];

  testGridColumns = [
    { key: 'slurryName', label: 'Slurry Name', sortable: true },
    { key: 'testTypeName', label: 'Test Type', sortable: true },
    { key: 'fileName', label: 'Document Name', sortable: true },
    { key: 'uploadedBy', label: 'Uploaded By', sortable: true },
    { key: 'description', label: 'Description', sortable: true }
  ];

  projectActions = [
    { label: 'Open', color: 'secondary' },
    { label: 'Edit', color: 'secondary' },
    { label: 'Delete', color: 'danger' }
  ];

  testActions = [
    { label: 'Open', color: 'secondary' }
  ];

  gridConfig = {
    sortable: true,
    paginate: false
  };
  
  isLoading = false;
  isEditPopupOpen = false;
  isUpdatingAttachment = false;
  selectedAttachmentForEdit: AttachmentDto | null = null;
  editedDescription = '';
  isUploadPopupOpen = false;
  isUploading = false;
  selectedFile: File | null = null;
  uploadDescription = '';
  isDragOver = false;
  isDirectUploading = false;
  directUploadError = '';
  
  readonly testTypeMap: Record<number, string> = {
    1: 'Thickening Time',
    2: 'Fluid Loss',
    3: 'Free Fluid',
    4: 'Rheology',
    5: 'HPHT Rheology',
    6: 'Static Gel Strength',
    20: 'Compressive Strength (UCA)',
    21: 'Compressive Strength (Crush)',
    24: 'Spacer Compatibility',
    33: 'Mixability',
    101: 'Rheology Downhole',
    102: 'Rheology Surface'
  };
  constructor(
    private service: ProjectDetailsService,
    private dialog: MatDialog
  ) {}
  ngOnInit(): void {
    if (this.projectId) {
      this.loadData();
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && this.projectId) {
      this.loadData();
    }
  }
  ngAfterViewInit(): void {
    this.projectDataSource.sort = this.projectSort;
    this.testDataSource.sort = this.testSort;
  }
  loadData() {
    this.isLoading = true;
    forkJoin({
      projectAttachments: this.service.getAttachmentsByProjectId(this.projectId),
      testAttachments: this.service.getTestAttachmentsByProjectId(this.projectId)
    }).subscribe({
      next: (res) => {
         const projectList = Array.isArray(res?.projectAttachments) ? res.projectAttachments : [];
      const testList = Array.isArray(res?.testAttachments) ? res.testAttachments : [];


        this.attachments = projectList;
        this.projectDataSource.data = this.attachments;
        this.testAttachments = testList.map(t => ({
          ...t,
          testTypeName: this.testTypeMap[t.testType || 0] || `Test Type ${t.testType}`,
          slurryName: t.slurryName || ''
        }));
        this.testDataSource.data = this.testAttachments;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading attachments:', err);
        this.isLoading = false;
      }
    });
  }
   

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.selectedFile = files[0];
    }
  }

  openFile(item: AttachmentDto | TestAttachmentDto, type: 'project' | 'test'): void {
    // Open the tab during the click event so browsers do not treat it as an unsolicited popup.
    const attachmentTab = window.open('', '_blank');
    if (!attachmentTab) {
      alert('Unable to open attachment. Please allow pop-ups for this site.');
      return;
    }

    attachmentTab.opener = null;
    const request$ =
      type === 'test'
        ? this.service.downloadTestAttachment(item.ownerId, item.id)
        : this.service.downloadProjectAttachment(this.projectId, item.id);

    request$.subscribe({
      next: (blob) => {
        const fileURL = URL.createObjectURL(blob);
        if (blob.type === 'application/octet-stream') {
          attachmentTab.close();
          const link = document.createElement('a');
          link.href = fileURL;
          link.download = item.fileName;
          link.click();
          URL.revokeObjectURL(fileURL);
          return;
        }

        attachmentTab.addEventListener('load', () => URL.revokeObjectURL(fileURL), { once: true });
        attachmentTab.location.href = fileURL;
      },
      error: (err) => {
        attachmentTab.close();
        console.error('Failed to open attachment:', err);
        alert('Failed to open attachment.');
      }
    });
  }

  editAttachment(item: AttachmentDto) {
    this.selectedAttachmentForEdit = item;
    this.editedDescription = item.description ?? '';
    this.isEditPopupOpen = true;
  }
  cancelEditAttachment() {
    this.isEditPopupOpen = false;
    this.isUpdatingAttachment = false;
    this.selectedAttachmentForEdit = null;
    this.editedDescription = '';
  }
  updateAttachmentDescription() {
    if (!this.selectedAttachmentForEdit || this.isUpdatingAttachment) {
      return;
    }
    const updateDto: UpdateAttachmentDto = {
      id: this.selectedAttachmentForEdit.id,
      fileName: this.selectedAttachmentForEdit.fileName,
      description: this.editedDescription ?? '',
      uploadedBy: this.selectedAttachmentForEdit.uploadedBy,
      ownerId: this.selectedAttachmentForEdit.ownerId
    };
    this.isUpdatingAttachment = true;
    this.service.updateAttachment(updateDto).subscribe({
      next: (success) => {
        if (success) {
          const targetId = this.selectedAttachmentForEdit?.id;
          this.attachments = this.attachments.map((a) =>
            a.id === targetId ? { ...a, description: this.editedDescription ?? '' } : a
          );
          this.projectDataSource.data = [...this.attachments];
          this.cancelEditAttachment();
        }
      },
      error: (err) => {
        console.error('Failed to update attachment description:', err);
        alert('Failed to update attachment. Please try again.');
        this.isUpdatingAttachment = false;
      }
    });
  }
  deleteAttachment(attachment: AttachmentDto) {
    const ref = this.dialog.open(DeleteConfirmationDialogComponent, {
      width: '420px',
      data: {
        title: 'Confirm Delete',
        message: `Delete "${attachment.fileName}"?`,
      }
    });

    ref.componentInstance.confirmRequested.subscribe(() => {
      ref.componentInstance.setDeleting(true);
      this.service.deleteAttachment(attachment.id).subscribe({
        next: (success) => {
          if (success) {
            this.attachments = this.attachments.filter(a => a.id !== attachment.id);
            this.projectDataSource.data = [...this.attachments];
          }
          ref.close(true);
        },
        error: (err) => {
          console.error('Delete failed:', err);
          alert('Failed to delete attachment');
          ref.componentInstance.setDeleting(false);
        }
      });
    });
  }



  onDropOnProjectSection(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    formData.append('file', files[0]);
    formData.append('projectId', this.projectId.toString());
    formData.append('description', '');
    formData.append('uploadedBy', 'User');

    this.directUploadError = '';
    this.isDirectUploading = true;

    this.service.uploadAttachment(formData).subscribe({
      next: () => {
        this.isDirectUploading = false;
        this.loadData();
      },
      error: () => {
        this.isDirectUploading = false;
        this.directUploadError = 'Upload failed. Please try again.';
      }
    });
  }

  openUploadPopup() {
    this.selectedFile = null;
    this.uploadDescription = '';
    this.isUploadPopupOpen = true;
  }

  closeUploadPopup() {
    this.isUploadPopupOpen = false;
    this.isUploading = false;
    this.selectedFile = null;
    this.uploadDescription = '';
  }

  onProjectActionClicked(event: { action: any; item: any }): void {
    switch (event.action.label) {
      case 'Open':
        this.openFile(event.item, 'project');
        break;
      case 'Edit':
        this.editAttachment(event.item);
        break;
      case 'Delete':
        this.deleteAttachment(event.item);
        break;
    }
  }

  onTestActionClicked(event: { action: any; item: any }): void {
    if (event.action.label === 'Open') {
      this.openFile(event.item, 'test');
    }
  }

  removeSelectedFile() {
    this.selectedFile = null;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    }
  }

uploadSelectedFile() {
  if (!this.selectedFile) {
    console.error('File not selected');
    return;
  }

  const formData = new FormData();

  formData.append('file', this.selectedFile);
  formData.append('projectId', this.projectId.toString());
  formData.append('description', this.uploadDescription || '');
  formData.append('uploadedBy', 'User');

  console.log('Sending FormData...');
  (formData as any).forEach((value: any, key: string) => {
    console.log(key, value);
  });

  this.isUploading = true;

  this.service.uploadAttachment(formData).subscribe({
    next: () => {
      console.log('Upload success');
      this.isUploading = false;
      this.loadData();
      this.closeUploadPopup();
    },
    error: (err) => {
      console.error('Upload failed:', err);
      this.isUploading = false;
    }
  });
}
}
